<template>
  <div>
    <AdArtefactSelection
      v-if="artefactSelection.tree"
      v-model="artefactSelection.open"
      :tree="artefactSelection.tree"
      :exclude="artefactSelection.selected"
      :treeFilters="artefactSelection.filters"
      @selected="artefactSelection.selectHandler($event)"
      :title="$t('select-concept')"
      :nothingSelectedText="$t('no-concept-selected')"
      :selectedItemTitle="artefactSelection.getSelectedItemTitle"
    >
      <template v-if="mode === 'questionnaire'" #details.content="{item}">
        <v-row dense>
          <v-col>
            <EditableString readonly :value="item.linkId" :label="$t('id')"/>
          </v-col>

          <v-col>
            <EditableQuestionnaireItemType
              :value="item.type"
              readonly
              :label="$t('type')"
            />
          </v-col>
        </v-row>
        <!-- Text -->
        <v-row dense>
          <v-col>
            <EditableTranslationEditor
              :value="item.text"
              :label="$t('text')"
              :locales="projectLocales"
              editor
              readonly
            />
          </v-col>
        </v-row>
        <v-row >
          <v-col cols="auto" class="mr-4">
            <EditableSwitch :value="item.repeats" :label="$t('repeats')" readonly/>
          </v-col>
          <v-col cols="auto" class="mr-4">
            <EditableSwitch :value="item.required" :label="$t('required')" readonly/>
          </v-col>
      </v-row>
      </template>
    </AdArtefactSelection>

    <EnableWhenAnswerConstructor
      v-model="answerConstructor.open"
      :answer="answerConstructor.selected"
      @save="answerConstructor.saveHandler($event)"
    />

    <v-card outlined :class="cardClasses">
      <!-- This is a duplicate button. Is it needed? -->
      <!-- <AdButton
        v-if="conditions.length === 0"
        class="ml-3"
        type="add"
        :text="$t('condition')"
        @click="addStructuralCondition"
      /> -->
      <v-data-table
        :headers="headers"
        :items="conditions"
        :items-per-page="conditions.length"
        hide-default-footer
        dense
        disable-sort
        disable-filtering
        disable-pagination
      >
        <template #top>
          <slot name="condition-table-top"></slot>
        </template>
        <template #header.actions>
          <AdButton type="add" list noMarginRight @click="addStructuralCondition"/>
        </template>

        <template #item.question="{item}">
          <em v-if="!item.question">{{ $t('no-concept-selected') }}</em>
          <span v-else>{{ getQuestionName(item.question) }}</span>
          <AdButton
            :type="item.question ? 'edit' : 'add'"
            :hint="!item.question ? $t('select-concept') : $t('edit')"
            @click="startSelectQuestion(item)"
            noMarginRight
            :list="!!item.question"
            small
          />
        </template>

        <template #item.operator="{item}">
          <v-select
            v-model="item.operator"
            :items="operators"
            hide-details
            class="pt-0 mt-0"

          >
            <template #selection="{item}">
              {{ item.value }}
            </template>
            <template #item="{item}">
              {{ item.value }}
            </template>
          </v-select>
        </template>

        <template #item.answer="{item}">
          <em v-if="!hasAnswer(item)">{{ $t('no-answer-added') }}</em>
          <StructuralConditionAnswer v-else :item="item" />
          <AdButton
            :type="hasAnswer(item) ? 'edit' : 'add'"
            @click="startEditAnswer(item)"
            noMarginRight
            :list="hasAnswer(item)"
            small
          />
        </template>

        <template #item.actions="{ item }">
          <AdSpeedDial direction="left">
            <AdButton
              type="erase"
              list
              @click="removeStructuralCondition(item)"
            />
          </AdSpeedDial>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import AdArtefactSelection from '@/components/Utilities/AdArtefactSelection'
import EnableWhenAnswerConstructor from '@/components/Scenario/EnableWhenAnswerConstructor'
import StructuralConditionAnswer from '@/components/Utilities/StructuralConditionAnswer'

// import { cloneDeep } from 'lodash'

export default {
  name: 'StructuralConditionEditor',
  components: {
    AdArtefactSelection,
    EnableWhenAnswerConstructor,
    StructuralConditionAnswer
  },
  props: {
    artefactSelectionItemText: {
      type: Function,
      required: false
    },
    cardClasses: {
      type: String,
      default: 'mb-5 py-1 px-3'
    },
    conditions: {
      type: Array,
      default: () => []
    },
    /**
     * Mode affects the way question names are resolved.
     */
    mode: {
      type: String,
      default: 'transaction',
      validator: v => ['questionnaire', 'transaction'].includes(v)
    },
    representingTemplate: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      answerConstructor: {
        open: false,
        selected: null,
        saveHandler: null
      },
      artefactSelection: {
        tree: {
          artefact: 'DE',
          loading: false,
          items: [],
          menu: false,
          insertable: false,
          editable: false,
          expandable: true,
          key: 'navkey',
          text: 'name.#text',
          children: 'concept'
        },
        selectHandler: null,
        open: false,
        selected: [],
        versionProp: null,
        filters: []
      }
    }
  },
  computed: {
    headers () {
      return [
        { value: 'question', text: this.$t('question') },
        { value: 'operator', text: this.$t('operator'), width: '1' },
        { value: 'answer', text: this.$t('answer') },
        { value: 'actions', align: 'right', width: '80px' }
      ]
    },
    answerChoices () {
      return this.$store.getters['server/getDecorTypes']?.data?.EnableWhenConditionType?.[0]?.element || []
    },
    getConceptName () {
      return id => {
        const item = this.$filters.listHelper.findItemInList({
          items: this.representingTemplate,
          children: 'concept',
          handler: match => match.id === id
        })
        if (Array.isArray(item?.name)) return this.$filters.getMatchingTranslation({ value: item.name })
        return id
      }
    },
    getQuestionnaireItemName () {
      return id => {
        const item = this.$filters.listHelper.findItemInList({
          items: this.representingTemplate,
          children: 'item',
          handler: match => match.linkId === id
        })
        if (Array.isArray(item?.text)) return this.$filters.getMatchingTranslation({ value: item.text })
        return id
      }
    },
    getQuestionName () {
      return id => {
        if (this.mode === 'transaction') {
          return this.getConceptName(id)
        }
        if (this.mode === 'questionnaire') {
          return this.getQuestionnaireItemName(id)
        }
        return id
      }
    },
    hasAnswer () {
      return item => this.answerChoices.some(choice => item[choice.name] != null)
    },
    operators () {
      return this.$store.getters['server/getDecorTypes']?.data?.ConditionOperatorType?.[0]?.enumeration || []
    },
    structuralConditions () {
      return cond => {
        if (!Array.isArray(cond.enableWhen)) return []
        return cond.enableWhen
      }
    }
  },
  methods: {
    addStructuralCondition () {
      const newItem = {
        question: null,
        operator: this.operators[0]?.value || null
      }
      this.conditions.push(newItem)
      this.startSelectQuestion(newItem)
    },
    removeStructuralCondition (item) {
      if (!Array.isArray(this.conditions)) return
      const i = this.conditions.indexOf(item)
      if (i > -1) {
        this.conditions.splice(i, 1)
      }
    },
    startSelectQuestion (item) {
      if (this.mode === 'transaction') {
        this.artefactSelection.selectHandler = (selected) => {
          item.question = selected.id
        }
      } else if (this.mode === 'questionnaire') {
        this.artefactSelection.selectHandler = (selected) => {
          item.question = selected.linkId
        }
      }
      this.artefactSelection.getSelectedItemTitle = this.artefactSelectionItemText
      this.artefactSelection.open = true
    },
    startEditAnswer (item) {
      this.answerConstructor.saveHandler = (answer) => {
        this.answerChoices.forEach(({ name }) => {
          if (answer[name]) {
            this.$set(item, name, answer[name])
          } else {
            delete this.$delete(item, name)
          }
        })
      }
      this.answerConstructor.selected = item
      this.answerConstructor.open = true
    },
    withUuid (arrWithObj) {
      if (!Array.isArray(arrWithObj)) return []
      return arrWithObj.map(obj => ({ ...obj, uuid: this.getUuid() }))
    }
  },
  watch: {
    representingTemplate: {
      immediate: true,
      handler (representingTemplate) {
        if (!Array.isArray(representingTemplate)) return

        if (this.mode === 'transaction') {
          this.artefactSelection.tree = {
            artefact: 'DE',
            loading: false,
            items: representingTemplate,
            menu: false,
            insertable: false,
            editable: false,
            expandable: true,
            key: 'navkey',
            text: 'name.#text',
            children: 'concept'
          }
        } else if (this.mode === 'questionnaire') {
          this.artefactSelection.tree = {
            artefact: 'QQ',
            loading: false,
            items: representingTemplate,
            menu: false,
            insertable: false,
            editable: false,
            expandable: true,
            key: 'linkId',
            text: 'text.#text',
            children: 'item'
          }
        }
      }
    }
  }
}
</script>
